import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { COLORS, DeltaEditor, FilePreviewPopup, Icon, PlanHuntDocAttachmentRow, PlanHuntTeamDocsPicker, type DocNode, type PlanHuntDocItem } from '@wehunt/ui'
import { ATTACHMENT_ACCEPT, type HuntAttachmentFile } from './useUploadHuntAttachment'

type DocFile = Extract<DocNode, { type: 'file' }>

export interface InvitationStepProps {
  /** Flat list of doc-tree nodes (mix of files and folders). The picker uses files only. */
  documents: DocNode[]
  participantCount: number
  /**
   * Invitation message as a JSON-serialized Quill Delta. Backward-compat:
   * any non-JSON string is rendered as plain text by the editor.
   * `null` means the user hasn't typed anything yet.
   */
  invitationMessage: string | null
  attachmentDocumentIds: string[]
  /** Locally-uploaded files attached via the OS file picker. */
  attachmentFiles: HuntAttachmentFile[]
  onMessageChange: (next: string | null) => void
  onAttachmentsChange: (next: string[]) => void
  onAttachmentFilesChange: (next: HuntAttachmentFile[]) => void
  /**
   * Optional handler that uploads an image picked via the editor's toolbar
   * and returns a URL to embed. Forwarded directly to the underlying
   * DeltaEditor; when omitted the image toolbar button is hidden.
   */
  onUploadImage?: (file: File) => Promise<string>
  /**
   * Optional handler that uploads a local file picked via the "+ Add
   * attachment" button and returns the descriptor to persist on the draft.
   * When omitted the button is hidden — the wizard relies on this being
   * provided once a draftId exists (after the first Continue / Save draft).
   */
  onUploadAttachment?: (file: File) => Promise<HuntAttachmentFile>
  /**
   * Resolve a storage path to a downloadable URL so an attached file can be
   * previewed in place — mirrors the view-hunt-plan behaviour. When omitted,
   * attachment rows are not clickable (no way to fetch the bytes).
   */
  onResolveAttachmentUrl?: (storagePath: string) => Promise<string>
}

export function InvitationStep({
  documents,
  participantCount,
  invitationMessage,
  attachmentDocumentIds,
  attachmentFiles,
  onMessageChange,
  onAttachmentsChange,
  onAttachmentFilesChange,
  onUploadImage,
  onUploadAttachment,
  onResolveAttachmentUrl,
}: InvitationStepProps) {
  const { t } = useTranslation('planHunt')
  const [teamDocPickerOpen, setTeamDocPickerOpen] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  // Attachment preview state — mirrors the view-hunt-plan OverviewTab so a
  // hunt leader can open a file they just attached. The host owns URL
  // resolution (Firebase Storage) via onResolveAttachmentUrl.
  const [previewFile, setPreviewFile] = useState<HuntAttachmentFile | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [previewError, setPreviewError] = useState<string | null>(null)

  const handlePreviewClick = async (file: HuntAttachmentFile) => {
    if (!onResolveAttachmentUrl) return
    setPreviewFile(file)
    setPreviewUrl(null)
    setPreviewError(null)
    try {
      setPreviewUrl(await onResolveAttachmentUrl(file.storagePath))
    } catch (err) {
      setPreviewError(err instanceof Error ? err.message : t('invitation.resolveUrlError'))
    }
  }

  const closePreview = () => {
    setPreviewFile(null)
    setPreviewUrl(null)
    setPreviewError(null)
  }

  const filesById = useMemo(() => {
    const m = new Map<string, DocFile>()
    for (const d of documents) if (d.type === 'file') m.set(d.id, d)
    return m
  }, [documents])

  const teamDocItems: PlanHuntDocItem[] = useMemo(
    () => Array.from(filesById.values()).map((f) => ({ id: f.id, name: f.name, kind: f.kind, size: f.size })),
    [filesById],
  )

  const attachedTeamDocs = attachmentDocumentIds.map((id) => ({
    id,
    file: filesById.get(id) ?? null,
  }))

  const detach = (id: string) => onAttachmentsChange(attachmentDocumentIds.filter((x) => x !== id))
  const detachFile = (storagePath: string) =>
    onAttachmentFilesChange(attachmentFiles.filter((f) => f.storagePath !== storagePath))

  const handleAddFileClick = () => {
    setUploadError(null)
    fileInputRef.current?.click()
  }

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    // Reset the input so the same file can be picked again after removal.
    e.target.value = ''
    if (!file || !onUploadAttachment) return
    setUploadError(null)
    setUploading(true)
    try {
      const descriptor = await onUploadAttachment(file)
      onAttachmentFilesChange([...attachmentFiles, descriptor])
    } catch (err) {
      const message = err instanceof Error ? err.message : t('invitation.uploadFailed')
      setUploadError(message)
    } finally {
      setUploading(false)
    }
  }

  const totalAttached = attachedTeamDocs.length + attachmentFiles.length

  type AttachmentRow = {
    key: string
    doc: PlanHuntDocItem
    source: 'team' | 'device'
    unavailable: boolean
    onRemove: () => void
    onView?: () => void
  }
  const attachmentRows: AttachmentRow[] = [
    ...attachedTeamDocs.map(({ id, file }) => ({
      key: `team-${id}`,
      doc: { id, name: file?.name ?? id, kind: (file?.kind ?? 'doc') as PlanHuntDocItem['kind'] },
      source: 'team' as const,
      unavailable: !file,
      onRemove: () => detach(id),
    })),
    ...attachmentFiles.map((f) => ({
      key: `file-${f.storagePath}`,
      doc: { id: f.storagePath, name: f.fileName, kind: kindFromFileName(f.fileName) },
      source: 'device' as const,
      unavailable: false,
      onRemove: () => detachFile(f.storagePath),
      onView: onResolveAttachmentUrl ? () => handlePreviewClick(f) : undefined,
    })),
  ]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, maxWidth: 700 }}>

      {/* Recipient info */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 14px',
          borderRadius: 8,
          background: COLORS.primaryContainer,
          border: `1px solid ${COLORS.primaryFixedDim}`,
          fontSize: 13,
          color: COLORS.primary,
        }}
      >
        <Icon name="send" size={15} />
        <span>
          {t('invitation.sendingTo')} <strong>{participantCount}</strong> {t('invitation.hunter', { count: participantCount })}
        </span>
      </div>

      {/* Message editor */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.outline, textTransform: 'uppercase', letterSpacing: 0.3 }}>
          {t('invitation.messageLabel')}
        </div>
        <DeltaEditor
          value={invitationMessage}
          onChange={onMessageChange}
          onUploadImage={onUploadImage}
          placeholder={t('invitation.messagePlaceholder')}
          ariaLabel={t('invitation.messageAria')}
        />
      </div>

      {/* Attachments */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.outline, textTransform: 'uppercase', letterSpacing: 0.3 }}>
            {t('invitation.attachments')}
          </span>
          {totalAttached > 0 && (
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                padding: '1px 7px',
                borderRadius: 999,
                background: COLORS.primaryContainer,
                color: COLORS.primary,
              }}
            >
              {totalAttached}
            </span>
          )}
        </div>

        <div className="wh-card" style={{ overflow: 'hidden' }}>
          {attachmentRows.length > 0 ? (
            attachmentRows.map((r, i) => (
              <PlanHuntDocAttachmentRow
                key={r.key}
                doc={r.doc}
                source={r.source}
                unavailable={r.unavailable}
                last={i === attachmentRows.length - 1}
                onRemove={r.onRemove}
                onView={r.onView}
              />
            ))
          ) : (
            <div style={{ padding: '20px 16px', textAlign: 'center', fontSize: 13, color: COLORS.outline }}>
              {t('invitation.noAttachments')}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {onUploadAttachment && (
            <>
              <button
                type="button"
                className="wh-btn wh-btn-outline"
                onClick={handleAddFileClick}
                disabled={uploading}
              >
                <Icon name="upload" size={15} />
                {uploading ? t('invitation.uploading') : t('invitation.uploadFromDevice')}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept={ATTACHMENT_ACCEPT}
                style={{ display: 'none' }}
                onChange={handleFileSelected}
                aria-label={t('invitation.pickFile')}
              />
            </>
          )}
          {/* Always shown so the option is discoverable; disabled when the team
              has no documents to pick (CLUB-631, matching the prototype). */}
          <button
            type="button"
            className="wh-btn wh-btn-outline"
            onClick={() => setTeamDocPickerOpen(true)}
            disabled={teamDocItems.length === 0}
          >
            <Icon name="file" size={15} />
            {t('invitation.fromTeamDocuments')}
          </button>
        </div>

        {uploadError && (
          <div role="alert" style={{ fontSize: 12, color: COLORS.error, padding: '4px 2px' }}>
            {uploadError}
          </div>
        )}
      </div>

      {teamDocPickerOpen && (
        <PlanHuntTeamDocsPicker
          docs={teamDocItems}
          alreadyPicked={attachmentDocumentIds}
          onCancel={() => setTeamDocPickerOpen(false)}
          onConfirm={(ids) => {
            onAttachmentsChange([...attachmentDocumentIds, ...ids])
            setTeamDocPickerOpen(false)
          }}
        />
      )}

      {previewFile && (
        <FilePreviewPopup
          fileName={previewFile.fileName}
          mimeType={previewFile.mimeType}
          url={previewUrl}
          error={previewError}
          onClose={closePreview}
        />
      )}
    </div>
  )
}

function kindFromFileName(name: string): PlanHuntDocItem['kind'] {
  const ext = name.slice(name.lastIndexOf('.') + 1).toLowerCase()
  if (ext === 'pdf') return 'pdf'
  if (ext === 'xls' || ext === 'xlsx') return 'xls'
  if (ext === 'doc' || ext === 'docx') return 'doc'
  return 'doc'
}
